import React from 'react';
import classNames from 'classnames';

import { ensureListing } from '../../../util/data';
import { getBoatPrice } from '../../../util/dataExtractors';
import { getProductImages, isArrayLength, truncateText } from '../../../util/genericHelpers';
import { useIntl } from '../../../util/reactIntl';

import { AspectRatioWrapper, ResponsiveImage } from '../../../components';

import css from './SearchMapListingCard.module.css';
import useDamageWaiverConfig from '../../../hooks/useDamageWaiverConfig';

const FALLBACK_LISTING_TITLE = 'Untitled listing';
const FALLBACK_LISTING_URL = '#';

const getListingImageVariants = (image, variantPrefix) => {
  const imageVariants = image?.attributes?.variants || {};
  return Object.keys(imageVariants).filter(variant => variant.startsWith(variantPrefix));
};

const getListingPriceLabel = (listing, currentUser, damageWaiverConfig) => {
  const calculatedPrice = getBoatPrice(listing, currentUser, damageWaiverConfig);
  if (Number.isFinite(calculatedPrice)) {
    return `$${calculatedPrice.toFixed(2)}`;
  }

  const fallbackPrice = listing?.attributes?.price?.amount;
  return Number.isFinite(fallbackPrice) ? `$${(fallbackPrice / 100).toFixed(2)}` : null;
};

const SearchMapListingCard = props => {
  const intl = useIntl();
  const damageWaiverConfig = useDamageWaiverConfig();
  const {
    className,
    clickHandler,
    isInCarousel,
    listing,
    urlToListing,
    config,
    currentUser,
  } = props;

  const currentListing = ensureListing(listing);
  const { title: rawTitle, publicData = {} } = currentListing.attributes || {};
  const title = truncateText((rawTitle || FALLBACK_LISTING_TITLE).trim(), 28);
  const isInfoOnly = Boolean(publicData?.infoOnly);

  const firstImage = currentListing.images?.length > 0 ? currentListing.images[0] : null;
  const variantPrefix = config?.layout?.listingImage?.variantPrefix || 'listing-card';
  const variants = getListingImageVariants(firstImage, variantPrefix);
  const images = getProductImages(currentListing?.images);
  const showImagePlaceholder = !isArrayLength(images);
  const priceLabel = getListingPriceLabel(currentListing, currentUser, damageWaiverConfig);
  const noImageMessage = intl.formatMessage({ id: 'SearchMapInfoCard.noImage' });

  const classes = classNames(
    css.anchor,
    css.borderRadiusInheritTop,
    { [css.borderRadiusInheritBottom]: !isInCarousel },
    className
  );

  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();

    if (typeof clickHandler === 'function') {
      clickHandler(currentListing);
    }
  };

  return (
    <a
      className={classes}
      href={urlToListing || FALLBACK_LISTING_URL}
      onClick={handleClick}
      onDoubleClick={event => {
        event.stopPropagation();
      }}
    >
      <div
        className={classNames(css.card, css.borderRadiusInheritTop, {
          [css.borderRadiusInheritBottom]: !isInCarousel,
        })}
      >
        <AspectRatioWrapper className={css.aspectRatioWrapper} width={16} height={9}>
          <ResponsiveImage
            rootClassName={classNames(css.rootForImage, css.borderRadiusInheritTop)}
            alt={title}
            noImageMessage={noImageMessage}
            image={firstImage}
            variants={variants}
            showImagePlaceholder={showImagePlaceholder}
            sizes="250px"
          />
        </AspectRatioWrapper>

        {isInfoOnly ? (
          <>
            <div className={css.infoOnlyRow}>
              <div className={css.infoOnlyBadge}>Public Moorage</div>
            </div>
            <div
              className={classNames(css.info, {
                [css.borderRadiusInheritBottom]: !isInCarousel,
              })}
            >
              <div className={css.name}>{title}</div>
            </div>
          </>
        ) : (
          <div
            className={classNames(css.info, {
              [css.borderRadiusInheritBottom]: !isInCarousel,
            })}
          >
            {priceLabel ? <div className={css.price}>{priceLabel}</div> : null}
            <div className={css.name}>{title}</div>
          </div>
        )}
      </div>
    </a>
  );
};

export default SearchMapListingCard;
